import { useState, useMemo, useRef } from 'react'
import { useFinance } from '../../context/FinanceContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency } from '../../lib/formatters'
import { STATE_TAX_DATA } from '../../lib/calculations'
import { calculateCostOfLiving } from '../../lib/calculations/costOfLiving'
import { addChartToPDF } from '../../lib/pdfChartCapture'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { useSubscription } from '../../context/SubscriptionContext'
import { trackExportReport } from '../../lib/analytics'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'

function ResultCard({ label, value, color, highlight }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
    </GlassCard>
  )
}

const STATES = STATE_TAX_DATA
  .filter(s => s.code !== 'CUSTOM' && s.code !== 'NONE')
  .sort((a, b) => a.name.localeCompare(b.name))

export default function CostOfLiving() {
  const { state } = useFinance()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)
  const chartRef = useRef(null)

  const [currentState, setCurrentState] = useState('OH')
  const [targetState, setTargetState] = useState('CA')
  const [currentSalary, setCurrentSalary] = useState(75000)
  const [currentHousing, setCurrentHousing] = useState(1200)

  const result = useMemo(() => calculateCostOfLiving({
    currentState, targetState, currentSalary, currentHousing,
  }), [currentState, targetState, currentSalary, currentHousing])

  const currentStateName = STATE_TAX_DATA.find(s => s.code === currentState)?.name || currentState
  const targetStateName = STATE_TAX_DATA.find(s => s.code === targetState)?.name || targetState

  // Chart data
  const chartData = result.breakdown.map(b => ({
    category: b.category,
    [currentStateName]: b.current,
    [targetStateName]: b.target,
  }))

  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const pageWidth = doc.internal.pageSize.getWidth()
    const margin = 14

    // --- Page 1: Title, Inputs, Key Results, COL Index ---

    // Title
    doc.setFontSize(22)
    doc.setTextColor(0)
    doc.text('Cost of Living Comparison Report', margin, 22)
    doc.setFontSize(10)
    doc.setTextColor(128)
    doc.text(`Generated by Liquid Finance  |  ${new Date().toLocaleDateString()}`, margin, 30)

    // Section: Compare Cost of Living (Inputs)
    doc.setFontSize(14)
    doc.setTextColor(0)
    doc.text('Compare Cost of Living', margin, 42)

    const inputRows = [
      ['Current State', currentStateName],
      ['Target State', targetStateName],
      ['Current Salary', formatCurrency(currentSalary)],
      ['Monthly Housing Cost', formatCurrency(currentHousing)],
    ]

    autoTable(doc, {
      head: [['Input', 'Value']],
      body: inputRows,
      startY: 46,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
    })

    // Section: Key Results (stat cards)
    let y = doc.lastAutoTable.finalY + 10
    doc.setFontSize(14)
    doc.setTextColor(0)
    doc.text('Key Results', margin, y)
    y += 4

    const keyResultRows = [
      ['Equivalent Salary', formatCurrency(result.equivalentSalary)],
      ['Salary Difference', `${result.salaryDifference >= 0 ? '+' : ''}${formatCurrency(result.salaryDifference)}`],
      ['COL Difference', `${result.percentDifference >= 0 ? '+' : ''}${result.percentDifference}%`],
      ['Tax Difference', `${result.taxDifference >= 0 ? '+' : ''}${formatCurrency(result.taxDifference)}/yr`],
    ]

    autoTable(doc, {
      head: [['Metric', 'Value']],
      body: keyResultRows,
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
    })

    // Section: Cost of Living Index (mirrors the side-by-side COL index cards)
    y = doc.lastAutoTable.finalY + 10
    doc.setFontSize(14)
    doc.setTextColor(0)
    doc.text('Cost of Living Index', margin, y)
    y += 4

    const colIndexRows = [
      [currentStateName, `${result.currentCOL}`, `${result.currentStateTaxRate}%`],
      [targetStateName, `${result.targetCOL}`, `${result.targetStateTaxRate}%`],
    ]

    autoTable(doc, {
      head: [['State', 'COL Index', 'State Tax Rate']],
      body: colIndexRows,
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
    })

    y = doc.lastAutoTable.finalY + 3
    doc.setFontSize(8)
    doc.setTextColor(120)
    doc.text('Index: US average = 100. Source: MERIC 2025 category-specific data.', margin, y)

    // Section: Annual Cost Comparison (category-by-category breakdown)
    y = doc.lastAutoTable.finalY + 12
    doc.setFontSize(14)
    doc.setTextColor(0)
    doc.text('Annual Cost Comparison', margin, y)
    y += 4

    const breakdownRows = result.breakdown.map(b => [
      b.category,
      formatCurrency(b.current),
      formatCurrency(b.target),
      `${b.target >= b.current ? '+' : ''}${formatCurrency(b.target - b.current)}`,
      `${b.target >= b.current ? '+' : ''}${b.current > 0 ? Math.round(((b.target - b.current) / b.current) * 100) : 0}%`,
    ])

    // Add totals row
    const totalCurrent = result.breakdown.reduce((s, b) => s + b.current, 0)
    const totalTarget = result.breakdown.reduce((s, b) => s + b.target, 0)
    breakdownRows.push([
      'TOTAL',
      formatCurrency(totalCurrent),
      formatCurrency(totalTarget),
      `${totalTarget >= totalCurrent ? '+' : ''}${formatCurrency(totalTarget - totalCurrent)}`,
      `${totalTarget >= totalCurrent ? '+' : ''}${totalCurrent > 0 ? Math.round(((totalTarget - totalCurrent) / totalCurrent) * 100) : 0}%`,
    ])

    // Check if we need a page break for the breakdown table
    if (y > doc.internal.pageSize.getHeight() - 60) {
      doc.addPage()
      y = margin
      doc.setFontSize(14)
      doc.setTextColor(0)
      doc.text('Annual Cost Comparison', margin, y)
      y += 4
    }

    autoTable(doc, {
      head: [['Category', currentStateName, targetStateName, 'Difference', '% Change']],
      body: breakdownRows,
      startY: y,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 3 },
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      didParseCell: (data) => {
        // Bold the total row
        if (data.row.index === breakdownRows.length - 1) {
          data.cell.styles.fontStyle = 'bold'
        }
      },
    })

    // Section: Summary Banner (mirrors the summary text shown at bottom of UI)
    y = doc.lastAutoTable.finalY + 10
    doc.setFontSize(12)
    doc.setTextColor(0)
    doc.text('Summary', margin, y)
    y += 6
    doc.setFontSize(10)
    doc.setTextColor(60)
    const summaryText = result.isCheaper
      ? `Moving from ${currentStateName} to ${targetStateName} could save you money. You'd need ${formatCurrency(result.equivalentSalary)} in ${targetStateName} to maintain the same purchasing power as ${formatCurrency(currentSalary)} in ${currentStateName}.`
      : `Moving from ${currentStateName} to ${targetStateName} would be more expensive. You'd need to earn ${formatCurrency(result.equivalentSalary)} in ${targetStateName} to match your ${formatCurrency(currentSalary)} purchasing power in ${currentStateName}.`
    const splitSummary = doc.splitTextToSize(summaryText, pageWidth - margin * 2)

    // Check page break
    if (y + splitSummary.length * 5 > doc.internal.pageSize.getHeight() - 10) {
      doc.addPage()
      y = margin
    }
    doc.text(splitSummary, margin, y)

    // Chart capture (adds its own page)
    await addChartToPDF(doc, chartRef, { title: 'Annual Cost Comparison Chart' })

    doc.save(`cost-of-living-comparison-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Inputs */}
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h3 className="text-lg font-semibold text-heading">Compare Cost of Living</h3>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Current State</label>
            <select
              value={currentState}
              onChange={e => setCurrentState(e.target.value)}
              className="w-full bg-input border border-input rounded-xl py-2.5 px-3 text-sm text-heading focus:outline-none focus:border-primary transition"
            >
              {STATES.map(s => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Target State</label>
            <select
              value={targetState}
              onChange={e => setTargetState(e.target.value)}
              className="w-full bg-input border border-input rounded-xl py-2.5 px-3 text-sm text-heading focus:outline-none focus:border-primary transition"
            >
              {STATES.map(s => (
                <option key={s.code} value={s.code}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Current Salary</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-faint text-sm">$</span>
              <input
                type="number"
                value={currentSalary}
                onChange={e => setCurrentSalary(parseFloat(e.target.value) || 0)}
                className="w-full bg-input border border-input rounded-xl py-2.5 pl-7 pr-3 text-sm text-heading focus:outline-none focus:border-primary transition"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-muted mb-1.5">Monthly Housing Cost</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-faint text-sm">$</span>
              <input
                type="number"
                value={currentHousing}
                onChange={e => setCurrentHousing(parseFloat(e.target.value) || 0)}
                className="w-full bg-input border border-input rounded-xl py-2.5 pl-7 pr-3 text-sm text-heading focus:outline-none focus:border-primary transition"
              />
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Key Results */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <ResultCard
          label="Equivalent Salary"
          value={formatCurrency(result.equivalentSalary)}
          color={colors.primary}
          highlight
        />
        <ResultCard
          label="Salary Difference"
          value={`${result.salaryDifference >= 0 ? '+' : ''}${formatCurrency(result.salaryDifference)}`}
          color={result.salaryDifference >= 0 ? colors.negative : colors.positive}
        />
        <ResultCard
          label="COL Difference"
          value={`${result.percentDifference >= 0 ? '+' : ''}${result.percentDifference}%`}
          color={result.isCheaper ? colors.positive : colors.negative}
          highlight
        />
        <ResultCard
          label="Tax Difference"
          value={`${result.taxDifference >= 0 ? '+' : ''}${formatCurrency(result.taxDifference)}/yr`}
          color={result.taxDifference <= 0 ? colors.positive : colors.negative}
        />
      </div>

      {/* COL Index Comparison */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">Cost of Living Index</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl border border-card">
            <p className="text-xs text-muted mb-1">{currentStateName}</p>
            <p className="text-2xl font-bold text-heading">{result.currentCOL}</p>
            <p className="text-xs text-muted mt-1">Tax rate: {result.currentStateTaxRate}%</p>
          </div>
          <div className="p-4 rounded-xl border border-card">
            <p className="text-xs text-muted mb-1">{targetStateName}</p>
            <p className="text-2xl font-bold text-heading">{result.targetCOL}</p>
            <p className="text-xs text-muted mt-1">Tax rate: {result.targetStateTaxRate}%</p>
          </div>
        </div>
        <p className="text-xs text-muted mt-3">Index: US average = 100. Source: MERIC 2025 category-specific data.</p>
      </GlassCard>

      {/* Side-by-Side Breakdown Chart */}
      <GlassCard>
        <h3 className="text-lg font-semibold text-heading mb-4">Annual Cost Comparison</h3>
        <div className="h-64" ref={chartRef}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
              <XAxis type="number" tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 11, fill: colors.chartTick }} />
              <YAxis type="category" dataKey="category" tick={{ fontSize: 11, fill: colors.chartTick }} width={90} />
              <Tooltip formatter={v => formatCurrency(v)} contentStyle={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: 12 }} />
              <Legend />
              <Bar dataKey={currentStateName} fill={colors.primary} radius={[0, 4, 4, 0]} />
              <Bar dataKey={targetStateName} fill={colors.warning} radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </GlassCard>

      {/* Summary Banner */}
      <GlassCard className="!p-4">
        <p className="text-sm text-heading">
          {result.isCheaper
            ? `Moving from ${currentStateName} to ${targetStateName} could save you money. You'd need ${formatCurrency(result.equivalentSalary)} in ${targetStateName} to maintain the same purchasing power as ${formatCurrency(currentSalary)} in ${currentStateName}.`
            : `Moving from ${currentStateName} to ${targetStateName} would be more expensive. You'd need to earn ${formatCurrency(result.equivalentSalary)} in ${targetStateName} to match your ${formatCurrency(currentSalary)} purchasing power in ${currentStateName}.`
          }
        </p>
      </GlassCard>
    </div>
  )
}
